import React, { useCallback, useEffect, useState } from "react";
import { useDropzone } from "react-dropzone";
import { api, getErrorMessage } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { UploadCloud, FileText, Check, Loader2, Mail, Copy, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

export default function Sas() {
  const nav = useNavigate();
  const [uploading, setUploading] = useState(false);
  const [uploaded, setUploaded] = useState([]);
  const [inboundEmail, setInboundEmail] = useState(null);
  const [regenerating, setRegenerating] = useState(false);

  useEffect(() => {
    api.get("/inbound/config")
      .then((r) => setInboundEmail(r.data.email))
      .catch(() => {});
  }, []);

  const copyEmail = () => {
    if (!inboundEmail) return;
    navigator.clipboard.writeText(inboundEmail);
    toast.success("Adresse copiée !");
  };

  const regenerate = async () => {
    if (!confirm("Régénérer l'adresse email ? L'ancienne ne fonctionnera plus.")) return;
    setRegenerating(true);
    try {
      const r = await api.post("/inbound/regenerate-token");
      setInboundEmail(r.data.email);
      toast.success("Nouvelle adresse générée.");
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setRegenerating(false);
    }
  };

  const onDrop = useCallback(async (accepted) => {
    if (accepted.length === 0) return;
    setUploading(true);
    try {
      const form = new FormData();
      accepted.forEach((f) => form.append("files", f));
      const r = await api.post("/documents/upload", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setUploaded((prev) => [...r.data, ...prev]);
      toast.success(`${r.data.length} fichier(s) déposé(s).`);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setUploading(false);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { "application/pdf": [".pdf"] },
    multiple: true,
  });

  return (
    <div className="min-h-screen">
      <PageHeader title="SAS — Dépôt de documents" subtitle="Glissez vos PDF ou utilisez l'envoi par e-mail." index="02" />
      <div className="p-6 md:p-10 space-y-8">
        <div
          {...getRootProps()}
          data-testid="sas-dropzone"
          className={`cursor-pointer border-2 border-dashed p-16 md:p-24 text-center transition-all ${
            isDragActive
              ? "border-[#002FA7] bg-[#002FA7]/5"
              : "border-zinc-300 bg-zinc-50/50 hover:border-[#002FA7] hover:bg-[#002FA7]/5"
          }`}
        >
          <input {...getInputProps()} data-testid="sas-dropzone-input" />
          <div className="flex flex-col items-center gap-4">
            {uploading ? (
              <Loader2 className="animate-spin text-[#002FA7]" size={44} strokeWidth={1.5} />
            ) : (
              <UploadCloud size={44} strokeWidth={1.2} className="text-zinc-400" />
            )}
            <div>
              <div className="text-xl font-semibold text-zinc-900">
                {uploading
                  ? "Dépôt en cours..."
                  : isDragActive
                  ? "Déposez les PDF ici"
                  : "Glissez vos PDF dans le SAS"}
              </div>
              <div className="mt-2 text-sm text-zinc-500">
                Ou <span className="underline">cliquez pour parcourir</span> · PDF uniquement · 25 Mo max
              </div>
            </div>
          </div>
        </div>

        {/* Email inbound (Resend) */}
        <div className="grid grid-cols-1 md:grid-cols-3 border border-zinc-200" data-testid="sas-email-inbound">
          <div className="md:col-span-2 p-6 border-r border-zinc-200">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-mono">
              <Mail size={12} strokeWidth={1.5} /> Dépôt par e-mail
            </div>
            <div className="mt-3 text-sm text-zinc-700">
              Envoyez un e-mail avec vos PDF en pièce jointe à l'adresse ci-dessous.
              Ils arriveront automatiquement dans la file "À classer".
            </div>
            <div className="mt-4 flex items-center gap-2 flex-wrap">
              {inboundEmail ? (
                <div
                  data-testid="sas-inbound-email"
                  className="flex items-center gap-3 bg-zinc-50 border border-zinc-200 px-3 py-2"
                >
                  <span className="font-mono text-xs text-zinc-800 select-all">{inboundEmail}</span>
                  <button
                    data-testid="sas-copy-email"
                    onClick={copyEmail}
                    title="Copier l'adresse"
                    className="text-zinc-400 hover:text-zinc-700 transition-colors"
                  >
                    <Copy size={13} strokeWidth={1.5} />
                  </button>
                </div>
              ) : (
                <div className="bg-zinc-50 border border-zinc-200 px-3 py-2 font-mono text-xs text-zinc-400 animate-pulse">
                  Chargement…
                </div>
              )}
              <button
                data-testid="sas-regenerate-token"
                onClick={regenerate}
                disabled={regenerating}
                title="Régénérer l'adresse"
                className="flex items-center gap-1.5 text-xs text-zinc-500 hover:text-zinc-800 transition-colors disabled:opacity-40"
              >
                <RefreshCw size={12} strokeWidth={1.5} className={regenerating ? "animate-spin" : ""} />
                Régénérer
              </button>
            </div>
          </div>
          <div className="p-6">
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-mono">Après dépôt</div>
            <div className="mt-3 text-sm text-zinc-700">
              Ouvrez la file "À classer" pour affecter les métadonnées et déclencher l'OCR.
            </div>
            <button
              data-testid="sas-go-queue"
              onClick={() => nav("/queue")}
              className="mt-4 bg-[#002FA7] hover:bg-[#00227A] text-white text-sm font-medium px-5 py-2.5 transition-colors"
            >
              Aller à la file →
            </button>
          </div>
        </div>

        {uploaded.length > 0 && (
          <div className="border border-zinc-200" data-testid="sas-uploaded-list">
            <div className="px-6 py-3 border-b border-zinc-200 bg-zinc-50/50 text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-mono">
              Dépôts récents ({uploaded.length})
            </div>
            <ul>
              {uploaded.map((d) => (
                <li
                  key={d.id}
                  className="flex items-center justify-between px-6 py-3 border-b border-zinc-100 last:border-0 animate-fade-in-up"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <FileText size={16} strokeWidth={1.5} className="text-zinc-500 shrink-0" />
                    <span className="text-sm truncate">{d.original_filename}</span>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-[#16A34A] font-mono">
                    <Check size={14} strokeWidth={2} /> déposé
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}
